import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, OnDestroy, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveContainer } from '../../../core/containers/reactive-container';
import { FilterDateSelectValue } from '../../../core/entities/filterDateSelect/filter-date-select-value';
import { SortField, SortValue } from '../../../core/entities/sort/sort';
import { FilterStateService } from '../../../core/services/filter-state/filter-state.service';
import { SidebarService } from '../../../core/services/sidebar/sidebar.service';
import { Reloadable } from '../../../core/services/command/command.service';
import { RefetchCoordinator } from '../../../core/services/refetch-coordinator/refetch-coordinator.service';
import { RolesService } from '../../../core/services/roles/roles.service';
import { debounceTime, distinctUntilChanged, fromEvent, map } from 'rxjs';
import { Task } from 'src/app/rent-module/shared/entities/task/task';
import { FieldConfig } from 'src/app/shared-module/components/field-selector/field-selector.component';
import { LocationEntity } from 'src/app/shared-module/shared/entities/location/location';
import { LocationService } from '../../shared/services/location/location.service';
import { TaskTypeService } from '../../shared/services/taskType/taskType.service';
import { isValidMyTaskListFilterState, MyTaskListFilterState } from './my-task-list-filter-state.interface';
import * as fromGraphQl from './graphql';

@Component({
  selector: 'my-tasks-list',
  templateUrl: './my-tasks.component.html',
  styleUrls: ['./my-tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyTasksComponent implements Reloadable, OnDestroy {

  // Injected services
  readonly sidebarService = inject(SidebarService);
  readonly rolesService = inject(RolesService);
  private readonly locationService = inject(LocationService);
  private readonly taskTypeService = inject(TaskTypeService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private readonly filterStateService = inject(FilterStateService);
  private readonly destroyRef = inject(DestroyRef);

  // Filter persistence (with validation - invalid state is automatically cleared)
  private readonly FILTER_STORAGE_KEY = 'my-task-list-filters';
  private readonly savedState = this.filterStateService.restore<MyTaskListFilterState>(
    this.FILTER_STORAGE_KEY,
    isValidMyTaskListFilterState
  );

  // Pagination signals
  readonly pageNumber = signal(0);
  readonly pageSize = signal(this.savedState?.pageSize ?? 15);

  // Sort fields and model
  readonly sortFields: SortField[] = [
    { label: 'Typ', fieldName: 'type', key: 'type' },
    { label: 'Fällig', fieldName: 'dueDateTimePeriod', key: 'due' }
  ];
  readonly sortModel = signal<SortValue>(this.restoreSortModel());

  // Filter signals (restored from saved state if available)
  readonly typesModel = signal<string[]>(this.savedState?.typeCodes ?? []);
  readonly locationsModel = signal<LocationEntity[]>(this.savedState?.locationIds as any ?? []);
  readonly dueDateModel = signal<FilterDateSelectValue | null>(this.savedState?.dueDate ?? null);

  // Field configuration for dynamic display/hide
  readonly availableFields: FieldConfig[] = [
    { field: 'address', label: 'Adresse' },
    { field: 'order', label: 'Auftrag' },
    { field: 'customer', label: 'Kunde' },
    { field: 'resources', label: 'Ressourcen' },
    { field: 'postalCode', label: 'Postleitzahl' },
    { field: 'city', label: 'Stadt' }
  ];

  readonly displayedFields = signal<string[]>(
    this.savedState?.displayedFields ?? ['address', 'order', 'customer', 'resources']
  );

  readonly showMetadata = signal<boolean>(
    this.savedState?.showMetadata ?? true
  );

  // Global search signal
  readonly globalSearchText = signal<string>('');

  // Global search input reference
  readonly globalSearchInput = viewChild<ElementRef<HTMLInputElement>>('globalSearch');

  // Load options via toSignal
  readonly locationsOptions = toSignal(
    this.locationService.getLocations().pipe(
      map(locations => locations ? LocationEntity.createLocations(locations) : [])
    ),
    { initialValue: [] }
  );

  readonly typeOptions = toSignal(
    this.taskTypeService.getTaskTypes().pipe(
      map(taskTypes => taskTypes.map(t => ({ id: t.code, name: t.name, label: t.name })))
    ),
    { initialValue: [] }
  );

  // Refetch coordinator registration (list view - no entityId)
  private readonly unregisterRefetch = this.refetchCoordinator.register(
    this,
    Task.ENTITY_TYPE
  );

  constructor() {
    // Global search input debounce with proper cleanup
    effect(() => {
      const inputEl = this.globalSearchInput();
      if (inputEl) {
        fromEvent(inputEl.nativeElement, 'input').pipe(
          map((event: Event) => (event.target as HTMLInputElement).value),
          debounceTime(300),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(value => {
          this.globalSearchText.set(value);
          this.pageNumber.set(0);
        });
      }
    });

    // Auto-save filter state when any filter changes
    effect(() => {
      // Read all filter signals to track them
      const sortValue = this.sortModel();
      const state: MyTaskListFilterState = {
        typeCodes: this.typesModel(),
        locationIds: this.locationsModel() as any,
        dueDate: this.dueDateModel(),
        sort: { key: sortValue.field.key, direction: sortValue.direction },
        pageSize: this.pageSize(),
        displayedFields: this.displayedFields(),
        showMetadata: this.showMetadata()
      };

      // Use untracked to avoid circular dependency
      untracked(() => {
        this.filterStateService.save(this.FILTER_STORAGE_KEY, state);
      });
    });
  }

  /**
   * Restore sort model from saved state, matching by key
   */
  private restoreSortModel(): SortValue {
    if (this.savedState?.sort?.key) {
      const field = this.sortFields.find(f => f.key === this.savedState!.sort.key);
      if (field) {
        return {
          field,
          direction: this.savedState.sort.direction || 'asc'
        };
      }
    }
    // Default: no sorting (id asc)
    return {
      field: this.sortFields[0],
      direction: 'asc'
    };
  }

  // ReactiveContainer replaces MyTasksListDataSource
  private readonly container = new ReactiveContainer<fromGraphQl.MyTasksListQueryResponse>(
    fromGraphQl.MY_TASKS_LIST_QUERY,
    {
      useGlobalLoading: false,
      pollInterval: 60000,
      stopPollingOnSuccess: false
    }
  );

  // Reactive query - auto-refetches when any filter signal changes
  readonly data = this.container.fromSignals(
    [
      this.pageNumber, this.pageSize, this.sortModel,
      this.typesModel, this.locationsModel,
      this.dueDateModel, this.globalSearchText
    ],
    ([page, size, sortValue, types, locations, dueDate, searchText]) => ({
      pageNumber: page,
      pageSize: size,
      sortProperty: sortValue.field.fieldName,
      sortDirection: sortValue.direction,
      types: types.length > 0 ? types : undefined,
      locationIds: locations?.length > 0 ? locations : undefined,
      fromDueDateTime: dueDate?.fromDate || undefined,
      toDueDateTime: dueDate?.toDate || undefined,
      includeOverDue: dueDate?.includeOverDue ?? false,
      text: searchText || undefined
    })
  );

  // Derived signals from container
  readonly tasks = this.container.selectWithDefault(
    d => Task.createTasks(d.myTasks?.content ?? []),
    []
  );

  readonly totalElements = this.container.selectWithDefault(
    d => d.myTasks?.totalElements ?? 0,
    0
  );

  readonly loading = this.container.loading;

  // For Reloadable interface
  readonly reloadable: Reloadable = this;

  reload(): void {
    console.log('MyTasksComponent: reload triggered');
    this.container.reload();
  }

  ngOnDestroy(): void {
    this.unregisterRefetch();
    // Close sidebar when leaving
    if (this.sidebarService.isOpen()) {
      this.sidebarService.close();
    }
  }

  onPageChange(page: number): void {
    this.pageNumber.set(page);
  }

  onPageSizeChange(newPageSize: number): void {
    this.pageSize.set(newPageSize);
    this.pageNumber.set(0); // Reset to first page when changing page size
  }

  onSortChange(sortValue: SortValue): void {
    this.sortModel.set(sortValue);
    this.pageNumber.set(0);
  }

  onTypesChange(types: string[]): void {
    this.typesModel.set(types);
    this.pageNumber.set(0);
  }

  onLocationsChange(locations: LocationEntity[]): void {
    this.locationsModel.set(locations);
    this.pageNumber.set(0);
  }

  onDueDateChange(): void {
    this.pageNumber.set(0);
  }
}
